import React, { useMemo, useState } from 'react';
import { BankAccount, Debt, RecurringExpense } from '../types';
import { ShieldAlert, Info } from 'lucide-react';

interface StressTesterProps {
  bankAccounts: BankAccount[];
  debts: Debt[];
  expenses: RecurringExpense[];
}

export default function StressTester({ bankAccounts, debts, expenses }: StressTesterProps) {
  const [scenario, setScenario] = useState<'job_loss' | 'normal'>('normal');

  const { liquidCash, monthlyBurnRate, runwayMonths } = useMemo(() => {
    // 1. Liquid Cash (Checking + Savings only, excluding locked investments)
    const liquidCash = bankAccounts
      .filter(a => a.type === 'checking' || a.type === 'savings')
      .reduce((sum, a) => sum + (a.currentBalance || 0), 0);

    // 2. Monthly Burn Rate (Fixed Expenses + Minimum Debt Payments)
    const getMonthlyAmount = (amount: number, frequency: string) => {
      if (frequency === 'monthly') return amount;
      if (frequency === 'semi-monthly') return amount * 2;
      if (frequency === 'bi-weekly') return amount * (26 / 12);
      if (frequency === 'weekly') return amount * (52 / 12);
      return amount;
    };

    const monthlyExpenses = expenses.reduce((sum, exp) => sum + getMonthlyAmount(exp.amount, exp.schedule?.frequency || 'monthly'), 0);
    // debt.monthlyMinimum used to be added as-is, unlike expenses — a debt
    // paid bi-weekly/weekly/semi-monthly (the "Payment Schedule / Frequency"
    // field every debt exposes) understated the true monthly burn, since
    // the stored amount is per-payment, not per-month.
    const monthlyDebtMins = debts.reduce((sum, debt) => sum + getMonthlyAmount(debt.monthlyMinimum || 0, debt.schedule?.frequency || 'monthly'), 0);
    
    const monthlyBurnRate = monthlyExpenses + monthlyDebtMins;

    // 3. Runway
    const runwayMonths = monthlyBurnRate > 0 ? (liquidCash / monthlyBurnRate) : 0;

    return { liquidCash, monthlyBurnRate, runwayMonths };
  }, [bankAccounts, debts, expenses]);

  if (scenario === 'normal') {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden p-6 mt-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-slate-400" /> Cash Flow Stress Tester
            </h3>
            <p className="text-sm text-slate-500 mt-1">Simulate financial emergencies like job loss.</p>
          </div>
          <button 
            onClick={() => setScenario('job_loss')}
            className="px-4 py-2 bg-rose-50 text-rose-600 font-semibold text-sm rounded-lg hover:bg-rose-100 transition-colors"
          >
            Run Scenario: Job Loss
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-rose-50 rounded-xl shadow-sm border border-rose-200 overflow-hidden p-6 mt-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="font-bold text-rose-900 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-600" /> EMERGENCY SCENARIO: Total Income Loss
          </h3>
          <p className="text-sm text-rose-700 mt-1">Assuming $0 household income starting tomorrow.</p>
        </div>
        <button 
          onClick={() => setScenario('normal')}
          className="px-4 py-2 bg-white text-slate-700 font-semibold text-sm rounded-lg hover:bg-slate-50 border border-slate-200 transition-colors shadow-sm"
        >
          Reset
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 border border-rose-100 shadow-sm">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Available Liquid Cash</div>
          <div className="text-2xl font-black text-slate-900">${liquidCash.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Info className="w-3 h-3"/> Checking & Savings</div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border border-rose-100 shadow-sm">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Monthly Burn Rate</div>
          <div className="text-2xl font-black text-rose-600">${monthlyBurnRate.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Info className="w-3 h-3"/> Mandatory Expenses + Min Debt Payments</div>
        </div>

        <div className="bg-rose-600 rounded-lg p-4 text-white shadow-sm border border-rose-700">
          <div className="text-xs font-bold text-rose-200 uppercase tracking-wider mb-1">Emergency Runway</div>
          <div className="text-2xl font-black">{runwayMonths.toFixed(1)} Months</div>
          <div className="text-xs text-rose-200 mt-1 flex items-center gap-1">Before running out of cash</div>
        </div>
      </div>

      <div className="bg-white/60 p-4 rounded-lg text-sm text-rose-900">
        <p>
          <strong>Verdict: </strong> 
          {runwayMonths < 3 && "You are in the DANGER ZONE. Experts recommend at least 3-6 months of runway. You must build an emergency fund or slash your burn rate immediately."}
          {runwayMonths >= 3 && runwayMonths < 6 && "You are in the YELLOW ZONE. You have enough cash to survive a brief job gap, but a prolonged recession could put you in debt."}
          {runwayMonths >= 6 && "You are in the GREEN ZONE. Your household is extremely resilient to job loss."}
        </p>
      </div>
    </div>
  );
}
